import { riunione } from '@/model/qsadminapi/QsAdminApiModuleModel'
import MeetingEditor from '@/routes/meetings/MeetingEditor'
import { RoleRouteObject, RouteFunctionParams } from '@/types'
import { RouteObject, redirect } from 'react-router-dom'
import { LoaderFunction, ActionFunction, defer } from 'react-router-typesafe'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { qtipologia_riunione } from '@/model/qsadminapi/QQsAdminApiModule'
import { getAzureUserInformation } from '@/auth/azure/azureManager'
import { Roles } from '@/auth/azure/Roles'
import { ODataClientError } from '@odata2ts/http-client-api'

export const meetingEditorRouteLoader = (async ({ request, params }: RouteFunctionParams<'meetingId'>) => {
  const qsAdminApi = useQsAdminApiManager.getState().service
  let title
  let meeting
  if (params.meetingId === undefined || params.meetingId === null) {
    const userInfo = await getAzureUserInformation()
    const userEmail = userInfo?.email ?? ''
    const getAutore = await qsAdminApi.user().query((builder, impiegato) => {
      builder.filter(impiegato.email.eq(userEmail))
      builder.select('id', 'fullname')
    })

    meeting = {
      id: 0,
      ded_Dis: '',
      ded_RootFam: '',
      ded_SubFam: '',
      ded_Rev: '',
      ded_Id: 0,
      revisione: 0,
      data: null,
      creazione: new Date().toISOString(),
      argomento: null,
      anno_rif: new Date().getFullYear(),
      mainDocumentUrl: '',
      attachmentsFolderUrl: '',
      autore: getAutore.data.value[0],
    } satisfies riunione
    title = 'Nuova riunione'
  } else {
    //editor
    const meetingId = +params.meetingId

    const meetingData = await qsAdminApi
      .riunione(meetingId)
      .query((builder) => {
        builder.expanding('autore', (autoreBuilder, autore) => {
          autoreBuilder.select('id', 'fullname')
        })
        builder.expanding('tipologia', (tipologiaBuilder, tipologia) => {
          tipologiaBuilder.select('id', 'nome')
        })
        builder.expanding('impiegati', (impiegatiBuilder, impiegato) => {
          impiegatiBuilder.select('id', 'fullname')
          impiegatiBuilder.orderBy(impiegato.fullname.asc())
        })
      })
      .catch((error: ODataClientError) => {
        if (error.status === 404) throw new Error('404 Not Found: Riunione non trovata')
        throw new Error()
      })

    meeting = meetingData.data satisfies riunione
    title = `${meeting.ded_Dis}`
  }

  const getTipologiaRiunione = qsAdminApi.tipologia_riunione().query((builder) => {
    builder.select('id', 'nome')
    builder.orderBy(qtipologia_riunione.nome.asc())
  })
  const getQsImpiegato = qsAdminApi.user().query((builder, impiegato) => {
    builder.select('id', 'fullname')
    builder.orderBy(impiegato.fullname.asc())
  })

  return defer({
    title,
    meeting,
    getTipologiaRiunione,
    getQsImpiegato,
    getUserInfo: getAzureUserInformation(),
    defaultCRUDAllowedRoles: [
      Roles.GlobalAdministrator,
      Roles.Administrator,
      Roles.Supervisor,
      Roles.Sales,
      Roles.ExternalSales,
      Roles.Marketing,
      Roles.TechDeveloper,
    ],
  })
}) satisfies LoaderFunction

export const meetingEditorRouteAction = (async ({ request, params }) => {
  const meeting = (await request.json()) as riunione
  const qsAdminApi = useQsAdminApiManager.getState().service

  switch (request.method) {
    case 'POST': {
      const res = await qsAdminApi.riunione().create(meeting)
      return redirect(`../${res.data.id}`)
    }
    case 'PUT':
    case 'PATCH': {
      return await qsAdminApi.riunione(meeting.id).update(meeting)
    }
    default: {
      throw new Response('Method not allowed', {
        status: 405,
        statusText: 'Method not allowed',
      })
    }
  }
}) satisfies ActionFunction

export const MeetingEditorRoute: RouteObject = {
  path: ':meetingId',
  element: <MeetingEditor creating={false} />,
  loader: meetingEditorRouteLoader,
  action: meetingEditorRouteAction,
  allowedRoles: [Roles.Guest],
  handle: {
    crumb: (match) => ({
      label: match.data.meeting.ded_Dis,
    }),
  },
} as RoleRouteObject
